import { LineChart, DataPoint } from "./LineChart";
import { useReports } from "../utils/ReportsProvider";
import { bucketSeries, RawPoint } from "../utils/bucketingUtils";
import { UsersIcon, TrendingUp, TrendingDown } from "components";
import { useState } from "react";
import { useGetStatisticsQuery, reportsApi } from "services/reportsApi";
import { t } from "i18next";

const MetricCard = ({
  title,
  value,
  change,
  isPositive,
}: {
  title: string;
  value: string;
  change: string;
  isPositive: boolean;
}) => (
  <div className="relative my-8 bg-white p-4 shadow">
    <UsersIcon className="absolute right-2 top-2 h-10 w-10" />
    <div className="mb-2 pr-12">
      <span className="">{title}</span>
    </div>
    <div className="mb-2 text-3xl font-bold" style={{ color: "#1C67AD" }}>
      {value}
    </div>
    <div className="flex items-center text-sm">
      {change ? (
        <span className=" mr-1 flex items-center" style={{ color: isPositive ? "#87C38E" : "#E28D93" }}>
          {isPositive ? <TrendingUp className="mr-1 h-8 w-8" /> : <TrendingDown className="mr-1 h-8 w-8" />}
          <span className="text-2xl">{change}</span>
        </span>
      ) : (
        <span className="mr-1" style={{ color: "#87C38E" }}>
          N/A
        </span>
      )}
      <span className="text-xs text-gray-400">{t("reports.vsEquivalent")}</span>
    </div>
  </div>
);

export const ReportsTable = () => {
  const {
    activeTab,
    setActiveTab,
    showMultipleLines,
    setShowMultipleLines,
    visibleLines,
    setVisibleLines,
    apiDateRange,
  } = useReports();

  const [selectedDataPoint, setSelectedDataPoint] = useState<DataPoint | null>(null);
  const { data: statisticsData, isLoading: isStatisticsLoading } = useGetStatisticsQuery(apiDateRange);
  const [exportReport, { isLoading: isExporting }] = reportsApi.useExportReportMutation();

  const handleExportPDF = async () => {
    try {
      const response = await exportReport(apiDateRange).unwrap();
      const byteCharacters = atob(response.client_report_pdf_content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const metrics = statisticsData
    ? [
        {
          title: t("reports.totalSessions"),
          value: statisticsData.blocks_data.block_session.total_session.toString(),
          change: `${statisticsData.blocks_data.block_session.difference}%`,
          isPositive: statisticsData.blocks_data.block_session.difference >= 0,
        },
        {
          title: t("reports.totalClients"),
          value: statisticsData.blocks_data.block_total_client.total_clients.toString(),
          change: `${statisticsData.blocks_data.block_total_client.difference}%`,
          isPositive: statisticsData.blocks_data.block_total_client.difference >= 0,
        },
        {
          title: t("reports.newRegisteredClients"),
          value: statisticsData.blocks_data.block_new_registered_clients.new_registered_clients.toString(),
          change: `${statisticsData.blocks_data.block_new_registered_clients.difference}%`,
          isPositive: statisticsData.blocks_data.block_new_registered_clients.difference >= 0,
        },
        {
          title: t("reports.newGuestUsers"),
          value: statisticsData.blocks_data.block_new_guest_clients.new_guest_clients.toString(),
          change: `${statisticsData.blocks_data.block_new_guest_clients.difference}%`,
          isPositive: statisticsData.blocks_data.block_new_guest_clients.difference >= 0,
        },
      ]
    : [];

  // Raw points from API
  const rawChartData: RawPoint[] =
    statisticsData?.chart_data?.map((item: any) => ({
      date: item.date, // ISO
      value: item.total_sessions,
      totalUsers: item.total_clients,
      registeredUsers: item.registered_clients,
      guestUsers: item.guest_clients,
      newRegisteredUsers: item.new_registered_clients,
      newGuestUsers: item.new_guest_clients,
    })) || [];

  // Apply max-dots bucketing
  const MAX_POINTS = 40;
  const view: "sessions" | "users" = activeTab === "sessions" ? "sessions" : "users";
  const chartData: DataPoint[] = bucketSeries(rawChartData, MAX_POINTS, view);

  const handleTabChange = (tab: "sessions" | "users") => {
    setActiveTab(tab);
    setShowMultipleLines(tab === "users");
  };

  const legendItems = [
    { label: t("reports.totalClients"), color: "#1C67AD", key: "totalUsers" },
    { label: t("reports.registeredClients"), color: "#87C38E", key: "registeredUsers" },
    { label: t("reports.guestClients"), color: "#6b7280", key: "guestUsers" },
    { label: t("reports.newRegisteredClients"), color: "#8b5cf6", key: "newRegisteredUsers" },
    { label: t("reports.newGuestUsers"), color: "#E28D93", key: "newGuestUsers" },
  ];

  const handleLegendToggle = (key: string) => {
    setVisibleLines({
      ...visibleLines,
      [key]: !visibleLines[key],
    });
  };

  if (isStatisticsLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-lg" style={{ color: "#1C67AD" }}>
          {t("common.loading")}
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {metrics.map((metric, index) => (
          <MetricCard key={index} {...metric} />
        ))}
      </div>

      <div className="bg-white px-8 py-4 shadow">
        <div className="mb-6 flex space-x-4">
          <button
            onClick={() => handleTabChange("sessions")}
            className="px-4 py-2"
            style={{
              backgroundColor: activeTab === "sessions" ? "#1C67AD" : "transparent",
              color: activeTab === "sessions" ? "white" : "#1C67AD",
              border: activeTab === "sessions" ? "none" : "1px solid #d1d5db",
            }}
          >
            {t("reports.sessions")}
          </button>
          <button
            onClick={() => handleTabChange("users")}
            className="px-4 py-2"
            style={{
              backgroundColor: activeTab === "users" ? "#1C67AD" : "transparent",
              color: activeTab === "users" ? "white" : "#1C67AD",
              border: activeTab === "users" ? "none" : "1px solid #d1d5db",
            }}
          >
            {t("reports.totalClients")}
          </button>
        </div>

        {showMultipleLines && (
          <div className="mb-6 flex flex-wrap gap-4">
            {legendItems.map((item, index) => (
              <label key={index} className="flex cursor-pointer items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={visibleLines[item.key]}
                  onChange={() => handleLegendToggle(item.key)}
                  className="rounded"
                />
                <span className="h-3 w-3 rounded" style={{ backgroundColor: item.color }}></span>
                <span>{item.label}</span>
              </label>
            ))}
          </div>
        )}

        <div className="relative h-96">
          <LineChart
            data={chartData}
            showMultipleLines={showMultipleLines}
            visibleLines={visibleLines}
            onDataPointClick={setSelectedDataPoint}
          />

          {selectedDataPoint && (
            <div
              className="absolute z-10 rounded-lg border border-gray-300 bg-white p-3 shadow-lg"
              style={{
                left: (selectedDataPoint.x ?? 0) - 80,
                top: (selectedDataPoint.y ?? 0) - 40,
                minWidth: "160px",
              }}
            >
              <div className="mb-2 text-xs font-semibold">{selectedDataPoint.label ?? selectedDataPoint.date}</div>
              <div className="space-y-1 text-xs">
                {activeTab === "sessions" ? (
                  <div>
                    <span className="font-medium">{t("reports.sessions")}: </span>
                    <span>{selectedDataPoint.value}</span>
                  </div>
                ) : (
                  <>
                    <div>
                      <span className="font-medium">{t("reports.total")}: </span>
                      <span>{selectedDataPoint.totalUsers}</span>
                    </div>
                    <div>
                      <span className="font-medium">{t("reports.registered")}: </span>
                      <span>{selectedDataPoint.registeredUsers}</span>
                    </div>
                    <div>
                      <span className="font-medium">{t("reports.guests")}: </span>
                      <span>{selectedDataPoint.guestUsers}</span>
                    </div>
                    <div>
                      <span className="font-medium">{t("reports.newReg")}: </span>
                      <span>{selectedDataPoint.newRegisteredUsers}</span>
                    </div>
                    <div>
                      <span className="font-medium">{t("reports.newGuest")}: </span>
                      <span>{selectedDataPoint.newGuestUsers}</span>
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={() => setSelectedDataPoint(null)}
                className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 text-xs"
              >
                ×
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 flex justify-center">
        <button
          onClick={handleExportPDF}
          disabled={isExporting}
          className="px-6 py-2 text-white disabled:opacity-50"
          style={{ backgroundColor: "#1C67AD" }}
        >
          {isExporting ? t("common.loading") : t("reports.generateReport")}
        </button>
      </div>
    </div>
  );
};
